import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  Zap,
  AlertCircle,
  Bell,
  Search,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCurrency, formatDate, cn, getStatusColor } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";

// ============= STAT CARD =============

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  loading,
  accent,
}: {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  loading?: boolean;
  accent?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={cn("h-4 w-4", accent ?? "text-muted-foreground")} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-8 w-24 bg-muted animate-pulse rounded" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        {trend && !loading && (
          <div className={`flex items-center text-xs mt-1 ${trend.isPositive ? "text-green-600" : "text-red-600"}`}>
            {trend.isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(trend.value)}% from last month
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= AI INSIGHTS CARD =============

function AiInsightsCard({ navigate }: { navigate: (to: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["ai-lead-stats"],
    queryFn: async () => {
      // Use limit:100 (server max) to get leads for AI stats
      const res = await api.get("/crm/leads", { params: { limit: 100 } });
      const leads: Array<{ ai?: { band?: string } }> = (res as any).data || [];
      return {
        hot:      leads.filter((l) => l.ai?.band === "hot").length,
        warm:     leads.filter((l) => l.ai?.band === "warm").length,
        cold:     leads.filter((l) => l.ai?.band === "cold").length,
        unscored: leads.filter((l) => !l.ai?.band).length,
        total:    leads.length,
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">AI Lead Intelligence</CardTitle>
        <Zap className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex gap-4">
            {[1,2,3].map(i => <div key={i} className="h-8 w-14 bg-muted animate-pulse rounded" />)}
          </div>
        ) : (
          <>
            <div className="flex gap-4 mb-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-rose-600">{data?.hot ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">🔥 Hot</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{data?.warm ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">🌤 Warm</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-400">{data?.cold ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">❄️ Cold</p>
              </div>
              {(data?.unscored ?? 0) > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-300">{data?.unscored}</p>
                  <p className="text-[10px] text-muted-foreground">Unscored</p>
                </div>
              )}
            </div>
            {(data?.total ?? 0) === 0 && (
              <p className="text-xs text-muted-foreground">No leads yet — add your first lead in CRM</p>
            )}
            {(data?.hot ?? 0) > 0 && (
              <button onClick={() => navigate("/crm/leads")} className="text-xs text-primary hover:underline font-medium">
                View hot leads →
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============= RECENT JOBS CARD =============

function RecentJobsCard({ navigate }: { navigate: (to: string) => void }) {
  const { data: jobs, isLoading } = useQuery({
    queryKey: ["recent-jobs"],
    queryFn: async () => {
      const res = await api.get("/jobs", { params: { limit: 5, sortBy: "createdAt", sortOrder: "desc" } });
      return (res as any).data as Array<{
        _id: string;
        jobNumber?: string;
        title: string;
        status: string;
        client?: { name: string };
        dueDate?: string;
        totalAmount?: number;
      }>;
    },
    staleTime: 60 * 1000,
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between flex-row pb-3">
        <div>
          <CardTitle>Recent Jobs</CardTitle>
          <CardDescription>Latest job activity</CardDescription>
        </div>
        <button onClick={() => navigate("/jobs")} className="text-xs text-primary hover:underline">
          View all →
        </button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-10 bg-muted animate-pulse rounded" />)}
          </div>
        ) : !jobs || jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No jobs yet. Create your first job to get started.</p>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <div
                key={job._id}
                onClick={() => navigate(`/jobs`)}
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{job.title}</p>
                  <p className="text-xs text-muted-foreground">{job.client?.name ?? "—"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium border", getStatusColor(job.status))}>
                    {job.status.replace(/_/g, " ")}
                  </span>
                  {job.dueDate && (
                    <span className="text-[10px] text-muted-foreground hidden sm:block">
                      {formatDate(job.dueDate)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= RECENT ACTIVITY CARD =============

function RecentActivityCard() {
  const { data: payments, isLoading } = useQuery({
    queryKey: ["recent-payments-dashboard"],
    queryFn: async () => {
      const res = await api.get("/finance/dashboard");
      const d = (res as any).data;
      return (d?.recentPayments || []) as Array<{
        _id: string;
        amount: number;
        paymentDate: string;
        clientId?: { name: string };
        reference?: string;
      }>;
    },
    staleTime: 60 * 1000,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Recent Payments</CardTitle>
        <CardDescription>Latest received payments</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-10 bg-muted animate-pulse rounded" />)}
          </div>
        ) : !payments || payments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No payments recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p._id} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{(p.clientId as any)?.name ?? "—"}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(p.paymentDate)}</p>
                </div>
                <span className="text-sm font-semibold text-green-600 shrink-0 ml-2">
                  +{formatCurrency(p.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= OVERDUE INVOICES CARD =============

function OverdueCard({ navigate }: { navigate: (to: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["overdue-invoices-dashboard"],
    queryFn: async () => {
      const res = await api.get("/finance/invoices", {
        params: { status: "overdue", limit: 5, sortBy: "dueDate", sortOrder: "asc" },
      });
      return {
        invoices: ((res as any).data || []) as Array<{
          _id: string;
          invoiceNumber: string;
          client?: { name: string };
          balanceDue?: number;
          totalAmount?: number;
          dueDate: string;
        }>,
        pagination: (res as any).pagination,
      };
    },
    staleTime: 60 * 1000,
  });

  const total = data?.pagination?.total ?? 0;
  const invoices = data?.invoices ?? [];

  return (
    <Card className={total > 0 ? "border-red-200" : ""}>
      <CardHeader className="flex items-center justify-between flex-row pb-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            {total > 0 && <AlertCircle className="h-4 w-4 text-red-500" />}
            Overdue Invoices
          </CardTitle>
          <CardDescription>{total > 0 ? `${total} invoice${total !== 1 ? "s" : ""} need attention` : "All invoices up to date"}</CardDescription>
        </div>
        {total > 0 && (
          <button onClick={() => navigate("/invoices?status=overdue")} className="text-xs text-red-500 hover:underline">
            View all →
          </button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1,2].map(i => <div key={i} className="h-10 bg-muted animate-pulse rounded" />)}
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-green-600 text-center py-4 font-medium">✓ No overdue invoices</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => {
              const daysOverdue = Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86_400_000);
              return (
                <div key={inv._id} className="flex items-center justify-between p-2 rounded-md hover:bg-red-50 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{inv.client?.name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.invoiceNumber} · {daysOverdue}d overdue
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-red-600 shrink-0 ml-2">
                    {formatCurrency(inv.balanceDue ?? inv.totalAmount ?? 0)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= SMART ALERTS FEED =============

const SEVERITY_STYLES = {
  critical: "border-l-red-500 bg-red-50",
  warning: "border-l-amber-400 bg-amber-50",
  info: "border-l-blue-400 bg-blue-50",
};
const SEVERITY_DOT = { critical: "bg-red-500", warning: "bg-amber-400", info: "bg-blue-400" };

function SmartAlertsFeed({ navigate }: { navigate: (to: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["ai-alerts"],
    queryFn: async () => {
      const res = await api.get("/ai/alerts");
      return (res as any).data?.alerts as Array<{
        id: string; type: string; severity: "critical" | "warning" | "info";
        title: string; message: string; actionLabel: string; actionPath: string;
        entityLabel: string;
      }>;
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const alerts = data ?? [];
  const criticalCount = alerts.filter(a => a.severity === "critical").length;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between flex-row pb-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <CardTitle>Smart Alerts</CardTitle>
          {criticalCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {criticalCount}
            </span>
          )}
        </div>
        <CardDescription className="m-0">What needs attention now</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded" />)}
          </div>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-green-600 text-center py-6 font-medium">✓ All clear — no alerts right now</p>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={cn("border-l-4 rounded-r-md p-3 flex items-start justify-between gap-2", SEVERITY_STYLES[alert.severity])}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <span className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", SEVERITY_DOT[alert.severity])} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{alert.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{alert.message}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(alert.actionPath)}
                  className="text-xs text-primary hover:underline shrink-0 font-medium flex items-center gap-0.5"
                >
                  {alert.actionLabel} <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= REVENUE FORECAST CARD =============

function RevenueForecastCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["revenue-forecast"],
    queryFn: async () => {
      const res = await api.get("/ai/forecast");
      return (res as any).data as {
        buckets: Array<{ label: string; fromQuotes: number; fromJobs: number; fromInvoices: number; total: number; confidence: string }>;
        breakdown: {
          openQuotes: { count: number; weightedValue: number };
          activeJobs: { count: number; totalValue: number };
          pendingInvoices: { count: number; totalValue: number };
        };
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const confidence = { high: "text-green-600", medium: "text-amber-500", low: "text-slate-400" };
  const maxTotal = Math.max(...(data?.buckets.map(b => b.total) ?? [1]));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          Revenue Pipeline Forecast
        </CardTitle>
        <CardDescription>AI-weighted estimate from open quotes, jobs & invoices</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded" />)}
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground text-center py-4">No forecast data available</p>
        ) : (
          <div className="space-y-4">
            {/* Bucket bars */}
            {data.buckets.map((b) => (
              <div key={b.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Next {b.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{formatCurrency(b.total)}</span>
                    <span className={cn("text-[10px] font-medium uppercase", confidence[b.confidence as keyof typeof confidence])}>
                      {b.confidence}
                    </span>
                  </div>
                </div>
                <div className="flex h-2 rounded-full overflow-hidden bg-muted gap-px">
                  {b.fromInvoices > 0 && (
                    <div className="bg-green-500" style={{ width: `${(b.fromInvoices / maxTotal) * 100}%` }} title={`Invoices: ${formatCurrency(b.fromInvoices)}`} />
                  )}
                  {b.fromJobs > 0 && (
                    <div className="bg-blue-500" style={{ width: `${(b.fromJobs / maxTotal) * 100}%` }} title={`Jobs: ${formatCurrency(b.fromJobs)}`} />
                  )}
                  {b.fromQuotes > 0 && (
                    <div className="bg-amber-400" style={{ width: `${(b.fromQuotes / maxTotal) * 100}%` }} title={`Quotes (weighted): ${formatCurrency(b.fromQuotes)}`} />
                  )}
                </div>
                <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                  {b.fromInvoices > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"/>Invoices {formatCurrency(b.fromInvoices)}</span>}
                  {b.fromJobs > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/>Jobs {formatCurrency(b.fromJobs)}</span>}
                  {b.fromQuotes > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"/>Quotes {formatCurrency(b.fromQuotes)}</span>}
                </div>
              </div>
            ))}
            {/* Source breakdown */}
            <div className="border-t pt-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs font-semibold">{data.breakdown.openQuotes.count}</p>
                <p className="text-[10px] text-muted-foreground">Open Quotes</p>
                <p className="text-[10px] text-amber-600 font-medium">{formatCurrency(data.breakdown.openQuotes.weightedValue)} weighted</p>
              </div>
              <div>
                <p className="text-xs font-semibold">{data.breakdown.activeJobs.count}</p>
                <p className="text-[10px] text-muted-foreground">Active Jobs</p>
                <p className="text-[10px] text-blue-600 font-medium">{formatCurrency(data.breakdown.activeJobs.totalValue)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold">{data.breakdown.pendingInvoices.count}</p>
                <p className="text-[10px] text-muted-foreground">Pending Invoices</p>
                <p className="text-[10px] text-green-600 font-medium">{formatCurrency(data.breakdown.pendingInvoices.totalValue)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= ASK YOUR CRM =============

const QUICK_QUERIES = [
  { key: "hot-leads", label: "🔥 Hot Leads" },
  { key: "overdue-clients", label: "⚠️ Overdue Clients" },
  { key: "top-clients", label: "🏆 Top Clients" },
  { key: "expiring-quotes", label: "⏰ Expiring Quotes" },
];

function AskYourCRM() {
  const [question, setQuestion] = useState("");
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [mode, setMode] = useState<"nl" | "quick" | null>(null);

  const { data: result, isLoading, refetch } = useQuery({
    queryKey: ["ask-crm", activeQuery, mode],
    queryFn: async () => {
      if (!activeQuery) return null;
      if (mode === "quick") {
        const res = await api.get(`/ai/ask/quick/${activeQuery}`);
        return (res as any).data;
      } else {
        const res = await api.postAi("/ai/ask", { question: activeQuery });
        return (res as any).data;
      }
    },
    enabled: !!activeQuery,
    staleTime: 0,
  });

  const handleAsk = () => {
    if (!question.trim()) return;
    setActiveQuery(question.trim());
    setMode("nl");
  };

  const handleQuick = (key: string) => {
    setActiveQuery(key);
    setMode("quick");
  };

  const columns: string[] = result?.columns ?? [];
  const rows: Record<string, unknown>[] = result?.data ?? [];

  const formatCell = (val: unknown): string => {
    if (val == null) return "—";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (typeof val === "number") {
      // Treat as currency if it looks like a monetary amount (> 100 and no decimal weirdness)
      return val > 100 && Number.isFinite(val) ? formatCurrency(val) : val.toLocaleString();
    }
    if (typeof val === "string") {
      // ISO date string → readable date
      if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return formatDate(val);
      return val;
    }
    if (Array.isArray(val)) {
      // $lookup returns an array — map to names
      return val.map((v: any) =>
        v?.displayName ?? v?.name ?? v?.firstName
          ? `${v.firstName ?? ""} ${v.lastName ?? ""}`.trim()
          : v?.title ?? v?.invoiceNumber ?? v?.quotationNumber ?? String(v)
      ).join(", ") || "—";
    }
    if (typeof val === "object" && val !== null) {
      const obj = val as Record<string, unknown>;
      // MongoDB ObjectId
      if ("$oid" in obj) return String(obj.$oid);
      // Nested document — extract the most meaningful field
      if (obj.displayName) return String(obj.displayName);
      if (obj.name) return String(obj.name);
      if (obj.firstName) return `${obj.firstName} ${obj.lastName ?? ""}`.trim();
      if (obj.title) return String(obj.title);
      if (obj.invoiceNumber) return String(obj.invoiceNumber);
      if (obj.quotationNumber) return String(obj.quotationNumber);
      // Last resort: stringify but cap length
      return JSON.stringify(obj).slice(0, 60);
    }
    return String(val);
  };

  const formatColumnHeader = (col: string): string =>
    col
      .replace(/\./g, " ")
      .replace(/_/g, " ")
      // Split camelCase: "totalRevenue" → "Total Revenue"
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase())
      .trim();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Search className="h-4 w-4 text-primary" />
          Ask Your CRM
        </CardTitle>
        <CardDescription>Type a question or pick a quick query</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Natural language input */}
        <div className="flex gap-2">
          <input
            className="flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="e.g. Show me clients who haven't ordered in 3 months"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAsk(); }}
          />
          <button
            onClick={handleAsk}
            disabled={!question.trim() || isLoading}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-md font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            {isLoading && mode === "nl" ? "…" : "Ask"}
          </button>
        </div>

        {/* Quick query chips */}
        <div className="flex flex-wrap gap-2">
          {QUICK_QUERIES.map((q) => (
            <button
              key={q.key}
              onClick={() => handleQuick(q.key)}
              disabled={isLoading}
              className={cn(
                "text-xs px-3 py-1.5 rounded-full border font-medium transition-colors",
                activeQuery === q.key && mode === "quick"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground hover:border-primary/50"
              )}
            >
              {isLoading && activeQuery === q.key && mode === "quick" ? "Loading…" : q.label}
            </button>
          ))}
        </div>

        {/* Results */}
        {result && (
          <div className="rounded-md border overflow-hidden">
            <div className="bg-muted/30 px-3 py-2 flex items-center justify-between border-b">
              <p className="text-xs font-medium text-foreground">{result.answer}</p>
              <span className="text-[10px] text-muted-foreground">{rows.length} rows</span>
            </div>
            {rows.length > 0 && columns.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/20">
                      {columns.map(c => (
                        <th key={c} className="px-3 py-2 text-left font-medium text-muted-foreground">
                          {formatColumnHeader(c)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t hover:bg-muted/20">
                        {columns.map(c => {
                          const val = c.split(".").reduce((o: any, k) => o?.[k], row);
                          return <td key={c} className="px-3 py-2">{formatCell(val)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 10 && (
                  <p className="text-[10px] text-muted-foreground px-3 py-2 border-t">
                    Showing 10 of {rows.length} rows
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No results found</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============= MAIN DASHBOARD =============

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, tenant } = useAuthStore();

  // CRM stats
  const { data: crmStats, isLoading: crmLoading } = useQuery({
    queryKey: ["crm-statistics"],
    queryFn: async () => {
      const res = await api.get("/crm/statistics");
      return (res as any).data as { totalClients: number; totalLeads: number; activeClients: number };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Jobs stats
  const { data: jobStats, isLoading: jobsLoading } = useQuery({
    queryKey: ["jobs-statistics"],
    queryFn: async () => {
      const res = await api.get("/jobs/statistics");
      const d = (res as any).data as {
        byStatus: Record<string, { count: number; totalValue: number }>;
        overdue: number;
        thisMonth: number;
      };
      const byStatus = d?.byStatus || {};
      return {
        active:
          (byStatus.draft?.count || 0) +
          (byStatus.confirmed?.count || 0) +
          (byStatus.in_progress?.count || 0) +
          (byStatus.on_hold?.count || 0) +
          (byStatus.ready_for_delivery?.count || 0),
        completed: byStatus.completed?.count || 0,
        overdue: d?.overdue || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Finance stats
  const { data: financeStats, isLoading: financeLoading } = useQuery({
    queryKey: ["finance-dashboard-stats"],
    queryFn: async () => {
      const res = await api.get("/finance/dashboard");
      const d = (res as any).data;
      const invoiceSummary = d?.invoices?.summary || {};
      const pendingInvoices =
        (invoiceSummary.sent?.count || 0) +
        (invoiceSummary.viewed?.count || 0) +
        (invoiceSummary.partial?.count || 0);
      const revenue = d?.revenue || {};
      const thisMonthRevenue = revenue.thisMonth || 0;
      const thisYearRevenue = revenue.thisYear || 0;
      const monthsPassed = new Date().getMonth() + 1;
      const avgMonthly = monthsPassed > 0 ? thisYearRevenue / monthsPassed : 0;
      const revenueChange = avgMonthly > 0
        ? Math.round(((thisMonthRevenue - avgMonthly) / avgMonthly) * 1000) / 10
        : 0;
      return {
        pendingInvoices,
        overdueAmount: d?.invoices?.overdue?.totalAmount || 0,
        overdueCount: d?.invoices?.overdue?.count || 0,
        thisMonthRevenue,
        revenueChange,
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60_000,
  });

  const statsLoading = crmLoading || jobsLoading || financeLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back, {user?.firstName}! Here's what's happening with {tenant?.name}.
        </p>
      </div>

      {/* Primary stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Clients"
          value={crmStats?.totalClients ?? 0}
          description={`${crmStats?.activeClients ?? 0} active`}
          icon={Users}
          loading={crmLoading}
        />
        <StatCard
          title="Active Leads"
          value={crmStats?.totalLeads ?? 0}
          description="In pipeline"
          icon={LayoutDashboard}
          loading={crmLoading}
        />
        <StatCard
          title="Active Jobs"
          value={jobStats?.active ?? 0}
          description={jobStats?.overdue ? `${jobStats.overdue} overdue` : "In progress"}
          icon={Briefcase}
          loading={jobsLoading}
          accent={jobStats?.overdue ? "text-red-500" : undefined}
        />
        <StatCard
          title="Revenue (This Month)"
          value={formatCurrency(financeStats?.thisMonthRevenue ?? 0)}
          icon={DollarSign}
          loading={financeLoading}
          trend={
            financeStats?.revenueChange !== undefined && financeStats.revenueChange !== 0
              ? { value: Math.abs(financeStats.revenueChange), isPositive: financeStats.revenueChange > 0 }
              : undefined
          }
        />
      </div>

      {/* AI Insights */}
      <AiInsightsCard navigate={navigate} />

      {/* Secondary stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Completed Jobs"
          value={jobStats?.completed ?? 0}
          description="All time"
          icon={CheckCircle}
          loading={jobsLoading}
        />
        <StatCard
          title="Pending Invoices"
          value={financeStats?.pendingInvoices ?? 0}
          description="Awaiting payment"
          icon={Clock}
          loading={financeLoading}
        />
        <StatCard
          title="Overdue Amount"
          value={formatCurrency(financeStats?.overdueAmount ?? 0)}
          description={
            financeStats?.overdueCount
              ? `${financeStats.overdueCount} invoice${financeStats.overdueCount !== 1 ? "s" : ""}`
              : "No overdue invoices"
          }
          icon={TrendingDown}
          loading={financeLoading}
          accent={financeStats?.overdueAmount ? "text-red-500" : undefined}
        />
      </div>

      {/* Smart Alerts */}
      <SmartAlertsFeed navigate={navigate} />

      {/* Revenue Forecast + Recent data */}
      <div className="grid gap-6 md:grid-cols-2">
        <RevenueForecastCard />
        <div className="space-y-6">
          <RecentJobsCard navigate={navigate} />
          <RecentActivityCard />
        </div>
      </div>

      {/* Overdue invoices */}
      <OverdueCard navigate={navigate} />

      {/* Ask Your CRM */}
      <AskYourCRM />

      {/* Quick actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {[
            { label: "+ New Job", to: "/jobs" },
            { label: "+ New Invoice", to: "/invoices" },
            { label: "+ New Client", to: "/crm" },
            { label: "+ New Lead", to: "/crm/leads" },
            { label: "+ New Quotation", to: "/sales" },
          ].map((a) => (
            <button
              key={a.label}
              onClick={() => navigate(a.to)}
              className="text-sm text-primary border border-primary/30 rounded-md px-3 py-1.5 hover:bg-primary/5 transition-colors"
            >
              {a.label}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
